import type { Currency, Pillar, ProductCode, ProductFigure, ProductFigures, ProductMetric, ProductsOutput, Unit } from 'chery-contracts';
import { compatibleKpis, metricText } from './board-display';

/**
 * Presentation rules of the Products page's live figures and the Product bindings tab (Phase 8) — pure, so they are
 * unit-tested. Every figure arrives summed from `board.products`; nothing here recomputes one. An unbound or blank
 * figure is `—`, never 0.
 */

export const PRODUCT_METRICS: readonly ProductMetric[] = ['SOLD', 'DELIVERED'];

const FIELD: Readonly<Record<ProductMetric, 'sold' | 'delivered'>> = { SOLD: 'sold', DELIVERED: 'delivered' };

/** The figure of one metric on one product card. */
export const figureOf = (item: ProductFigures, metric: ProductMetric): ProductFigure => item[FIELD[metric]];

/** The figures of every card by product code. */
export const figuresByCode = (output: Pick<ProductsOutput, 'items'> | undefined): Map<ProductCode, ProductFigures> =>
  new Map((output?.items ?? []).map((item) => [item.code, item]));

export type FigureState = 'UNBOUND' | 'BLANK' | 'VALUE';

/** Unbound (show the bind hint), bound without a value in the period, or a value. */
export const figureState = (figure: ProductFigure | undefined): FigureState =>
  !figure || figure.kpiId === null ? 'UNBOUND' : figure.value === null ? 'BLANK' : 'VALUE';

/** `48.0`, or `—` for an unbound or blank figure. */
export const figureText = (figure: ProductFigure | undefined): string => metricText(figure?.value ?? null, 'NUMBER', null);

/**
 * The sum of one metric over every card — `null` when no card has a value — shown as "All models" so the per-model
 * figures can be checked against the total KPI.
 */
export const modelsTotal = (output: Pick<ProductsOutput, 'items'> | undefined, metric: ProductMetric): number | null => {
  const values = (output?.items ?? []).map((item) => figureOf(item, metric).value).filter((value): value is number => value !== null);

  return values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0);
};

/** `3 of 24 product figures unbound`, or `null` when all are bound. */
export const unboundCount = (output: Pick<ProductsOutput, 'unbound' | 'items'> | undefined): { count: number; total: number } | null => {
  if (!output || output.unbound.length === 0) {
    return null;
  }

  return { count: output.unbound.length, total: output.items.length * PRODUCT_METRICS.length };
};

/** The KPIs a product metric may be bound to: `NUMBER` only; pillar order, then name. */
export const compatibleProductKpis = <TKpi extends { name: string; pillar: Pillar; unit: Unit; currency: Currency | null }>(
  kpis: readonly TKpi[],
): TKpi[] => compatibleKpis({ expectedUnit: 'NUMBER', expectedCurrency: null }, kpis);
